import { Injectable, effect, signal, computed } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthService } from '@/services/auth.service';
import { ProfileDto } from '@/helper/dto/profile.dto';

export type ColorTheme = 'light' | 'dark' | 'system';

export interface layoutConfig {
    preset?: string;
    primary?: string;
    surface?: string | undefined | null;
    darkTheme?: boolean;
    menuMode?: string;
}

interface LayoutState {
    staticMenuDesktopInactive?: boolean;
    overlayMenuActive?: boolean;
    configSidebarVisible?: boolean;
    staticMenuMobileActive?: boolean;
    menuHoverActive?: boolean;
}

interface MenuChangeEvent {
    key: string;
    routeEvent?: boolean;
}

const THEME_STORAGE_KEY = 'cmp-color-theme';

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    _config: layoutConfig = {
        preset: 'Aura',
        primary: 'emerald',
        surface: null,
        darkTheme: false,
        menuMode: 'static'
    };

    _state: LayoutState = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false
    };

    layoutConfig = signal<layoutConfig>(this._config);
    layoutState = signal<LayoutState>(this._state);

    /** User preference: light, dark, or follow OS setting. */
    readonly colorTheme = signal<ColorTheme>(this.readStoredTheme());

    private readonly systemPrefersDark = signal(this.querySystemDark());

    readonly effectiveDark = computed(() => {
        const pref = this.colorTheme();
        if (pref === 'dark') return true;
        if (pref === 'light') return false;
        return this.systemPrefersDark();
    });

    private configUpdate = new Subject<layoutConfig>();
    private overlayOpen = new Subject<any>();
    private menuSource = new Subject<MenuChangeEvent>();
    private resetSource = new Subject();

    menuSource$ = this.menuSource.asObservable();
    resetSource$ = this.resetSource.asObservable();
    configUpdate$ = this.configUpdate.asObservable();
    overlayOpen$ = this.overlayOpen.asObservable();

    theme = computed(() => (this.layoutConfig()?.darkTheme ? 'light' : 'dark'));
    isSidebarActive = computed(() => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive);
    isDarkTheme = computed(() => this.layoutConfig().darkTheme);
    getPrimary = computed(() => this.layoutConfig().primary);
    getSurface = computed(() => this.layoutConfig().surface);
    isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

    transitionComplete = signal<boolean>(false);

    userProfile = new ProfileDto();

    private initialized = false;
    private lastAppliedDark: boolean | undefined;
    private profileThemeApplied = false;
    private systemMediaQuery = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

    constructor(private authService: AuthService) {
        this.systemPrefersDark.set(this.querySystemDark());
        this.ensureDefaultThemePreference();
        this.bindSystemPreferenceListener();
        this.bootstrapTheme();

        this.authService.sessionData.subscribe((session) => {
            if (!session.isLoggedIn) {
                this.profileThemeApplied = false;
                return;
            }

            this.userProfile = session.user?.profile ?? new ProfileDto();

            if (!this.profileThemeApplied) {
                const profileTheme = this.normalizeTheme(session.user?.profile?.colorTheme);
                if (profileTheme) {
                    this.setColorTheme(profileTheme, { fromProfile: true, persist: true });
                }
                this.profileThemeApplied = true;
            }
        });

        effect(() => {
            const pref = this.colorTheme();
            const dark =
                pref === 'dark' ? true : pref === 'light' ? false : this.systemPrefersDark();

            if (this.lastAppliedDark === dark && this.initialized) return;

            this.lastAppliedDark = dark;
            this.layoutConfig.update((config) =>
                config.darkTheme === dark ? config : { ...config, darkTheme: dark }
            );

            if (!this.initialized) {
                this.initialized = true;
                this.toggleDarkMode({ darkTheme: dark });
            } else {
                this.handleDarkModeTransition({ darkTheme: dark });
            }
            this.onConfigUpdate();
        });
    }

    private bootstrapTheme(): void {
        const dark = this.resolveDark(this.colorTheme());
        this.lastAppliedDark = dark;
        this.layoutConfig.update((config) => ({ ...config, darkTheme: dark }));
        this.toggleDarkMode({ darkTheme: dark });
        this.initialized = true;
        this.onConfigUpdate();
    }

    private ensureDefaultThemePreference(): void {
        if (typeof localStorage === 'undefined') return;
        if (this.hasStoredThemePreference()) return;
        localStorage.setItem(THEME_STORAGE_KEY, 'system');
        this.colorTheme.set('system');
    }

    private resolveDark(pref: ColorTheme): boolean {
        if (pref === 'dark') return true;
        if (pref === 'light') return false;
        return this.systemPrefersDark();
    }

    setColorTheme(
        theme: ColorTheme,
        options: { persist?: boolean; fromProfile?: boolean } = {}
    ): void {
        const { persist = true, fromProfile = false } = options;
        if (theme === 'system') {
            this.systemPrefersDark.set(this.querySystemDark());
        }
        this.colorTheme.set(theme);
        if (persist && typeof localStorage !== 'undefined') {
            localStorage.setItem(THEME_STORAGE_KEY, theme);
        }
        if (!fromProfile) {
            this.userProfile = { ...this.userProfile, colorTheme: theme };
        }
    }

    cycleColorTheme(): ColorTheme {
        const order: ColorTheme[] = ['system', 'light', 'dark'];
        const idx = order.indexOf(this.colorTheme());
        const next = order[(idx + 1) % order.length];
        this.setColorTheme(next);
        return next;
    }

    toggleDarkMode(config?: layoutConfig): void {
        const _config = config || this.layoutConfig();
        if (_config.darkTheme) {
            document.documentElement.classList.add('app-dark');
        } else {
            document.documentElement.classList.remove('app-dark');
        }
    }

    private normalizeTheme(value: string | null | undefined): ColorTheme | null {
        const normalized = value?.trim().toLowerCase();
        if (normalized === 'light' || normalized === 'dark' || normalized === 'system') {
            return normalized;
        }
        return null;
    }

    private readStoredTheme(): ColorTheme {
        if (typeof localStorage === 'undefined') return 'system';
        return this.normalizeTheme(localStorage.getItem(THEME_STORAGE_KEY)) ?? 'system';
    }

    hasStoredThemePreference(): boolean {
        if (typeof localStorage === 'undefined') return false;
        return this.normalizeTheme(localStorage.getItem(THEME_STORAGE_KEY)) != null;
    }

    private querySystemDark(): boolean {
        return !!this.systemMediaQuery?.matches;
    }

    private bindSystemPreferenceListener(): void {
        if (!this.systemMediaQuery) return;
        const handler = (event: MediaQueryListEvent) => {
            this.systemPrefersDark.set(event.matches);
        };
        this.systemMediaQuery.addEventListener('change', handler);
    }

    private handleDarkModeTransition(config: layoutConfig): void {
        if ((document as Document & { startViewTransition?: (cb: () => void) => { ready: Promise<void> } }).startViewTransition) {
            this.startViewTransition(config);
        } else {
            this.toggleDarkMode(config);
            this.onTransitionEnd();
        }
    }

    private startViewTransition(config: layoutConfig): void {
        const transition = (document as Document & { startViewTransition: (cb: () => void) => { ready: Promise<void> } }).startViewTransition(() => {
            this.toggleDarkMode(config);
        });

        transition.ready
            .then(() => {
                this.onTransitionEnd();
            })
            .catch(() => {});
    }

    private onTransitionEnd() {
        this.transitionComplete.set(true);
        setTimeout(() => {
            this.transitionComplete.set(false);
        });
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !this.layoutState().overlayMenuActive }));

            if (this.layoutState().overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive }));
        } else {
            this.layoutState.update((prev) => ({ ...prev, staticMenuMobileActive: !this.layoutState().staticMenuMobileActive }));

            if (this.layoutState().staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this._config = { ...this.layoutConfig() };
        this.configUpdate.next(this.layoutConfig());
    }

    onMenuStateChange(event: MenuChangeEvent) {
        this.menuSource.next(event);
    }

    reset() {
        this.resetSource.next(true);
    }
}